// The band edges of the depth look `chart` (round 145 card 02, R3278,
// ui-map-interface.md §17.3.09).
//
// The depth pass blends the band of each cell over its ring (the feathered
// field) and, in the look `chart`, cuts the blend at 1.5, 2.5 and 3.5: the
// band edges. This module traces the SAME field on the CPU, so the piece
// `depth_tints` can stand a thin isobath on each edge and break it for its
// figure. A shader cannot break a line for a figure, so the line is a batch.
//
// `bandFieldAt` is the twin of the blend in `shaders/depth.ts` `tint`: the
// kernel of the prelude over the cell and its six neighbours, a cell with no
// band adding nothing. `chartWeights` is the twin of the cut. The trace
// samples the field on a square lattice and walks it with marching squares,
// so a segment ends on the level at each lattice edge it crosses. A segment
// whose middle stands on a cell with no band is dropped, as the pass paints
// no tint there.
//
// The cost is a batch built once per hello, per survey pass and per flip of
// the look, and only while the look `chart` draws the tints. A lattice
// point on a cell whose ring holds one band alone takes that band with no
// kernel, because the blend of one band is that band.

import { CHART } from '../../../gen/presentation';
import { HEX_SIZE } from '../../../gen/balance';
import { COLS, ROWS, cellAt, cellCenterX, cellCenterY, neighborAt, worldSize } from '../../../world';

const T = CHART.pieces.depth_tints;

/** The levels of the blended band field the band edges stand on: halfway
 *  between two bands. Level k is the top of band k + 2. */
export const EDGE_LEVELS = [1.5, 2.5, 3.5] as const;

/** The band of one cell of the field, 0 for none: `writeBands` of the
 *  depth layer keeps 1 to 4 alone. */
function bandAt(bands: Uint8Array, c: number): number {
  const b = c >= 0 && c < bands.length ? bands[c] : 0;
  return b <= 4 ? b : 0;
}

/** The centre of each cell and its ring (the cell, then its six
 *  neighbours, -1 off the grid), built once for each grid. */
interface Geo { key: string; cx: Float64Array; cy: Float64Array; ring: Int32Array }
let geoCache: Geo | null = null;

function geo(): Geo {
  const key = `${COLS}x${ROWS}`;
  if (geoCache?.key === key) return geoCache;
  const n = COLS * ROWS;
  const cx = new Float64Array(n), cy = new Float64Array(n), ring = new Int32Array(7 * n);
  for (let c = 0; c < n; c++) {
    cx[c] = cellCenterX(c, COLS);
    cy[c] = cellCenterY(c, COLS);
    ring[7 * c] = c;
    for (let k = 0; k < 6; k++) ring[7 * c + 1 + k] = neighborAt(c, k, COLS, ROWS);
  }
  geoCache = { key, cx, cy, ring };
  return geoCache;
}

/**
 * The blended band at a world point and its gradient, into `out` as
 * [f, gx, gy]: the twin of the blend of `shaders/depth.ts`. Answers false
 * where the point is off the grid or no band of the ring reaches it.
 */
export function bandFieldAt(bands: Uint8Array, x: number, y: number, out: Float64Array): boolean {
  const c0 = cellAt(x, y, COLS, ROWS);
  return c0 >= 0 && fieldIn(geo(), bands, c0, x, y, out);
}

/** `bandFieldAt` at a point of cell `c0`. */
function fieldIn(g: Geo, bands: Uint8Array, c0: number, x: number, y: number, out: Float64Array): boolean {
  const r = T.fig.feather_k * HEX_SIZE;
  const invR2 = 1 / (r * r);
  let den = 0, nb = 0, dDx = 0, dDy = 0, dNx = 0, dNy = 0;
  for (let i = 0; i < 7; i++) {
    const c = g.ring[7 * c0 + i];
    if (c < 0) continue;
    const b = bandAt(bands, c);
    if (b === 0) continue;
    const vx = x - g.cx[c], vy = y - g.cy[c];
    const u = Math.max(0, 1 - (vx * vx + vy * vy) * invR2);
    const k = u * u;
    const dk = -4 * u * invR2;
    den += k; nb += k * b;
    dDx += dk * vx; dDy += dk * vy;
    dNx += dk * vx * b; dNy += dk * vy * b;
  }
  if (den <= 1e-6) return false;
  const f = nb / den;
  out[0] = f;
  out[1] = (dNx - f * dDx) / den;
  out[2] = (dNy - f * dDy) / den;
  return true;
}

/**
 * The share of each band at a point of the look `chart`, shelf to abyss,
 * from the blended band `f`, its gradient length `gn` and one device pixel
 * `px` in world px: each level is a crisp edge one pixel wide. The twin of
 * `chartWeights` of the depth pass.
 */
export function chartWeights(f: number, gn: number, px: number): [number, number, number, number] {
  const s = EDGE_LEVELS.map((lv) => gn > 1e-6
    ? Math.min(1, Math.max(0, (f - lv) / (gn * px) + 0.5))
    : (f >= lv ? 1 : 0));
  return [1 - s[0], s[0] - s[1], s[1] - s[2], s[2]];
}

/** The figure of the edge at level index `k`: the top of the deeper band
 *  in metres (`[chart.piece.soundings] band_top_m`). */
export function edgeFigure(k: number): string {
  return String(CHART.pieces.soundings.arr.band_top_m[k + 1] ?? 0);
}

/** The band edges of one field: the segments of each level, x0 y0 x1 y1. */
export interface EdgeTrace {
  segs: [number[], number[], number[]];
}

/**
 * For each cell: 0 when no cell of its ring holds a band, the band when
 * every cell of the ring that holds one holds the same band, and 255 for
 * a ring of two bands or more.
 */
function ringBands(g: Geo, bands: Uint8Array): Uint8Array {
  const out = new Uint8Array(COLS * ROWS);
  for (let c = 0; c < out.length; c++) {
    let one = bandAt(bands, c);
    for (let k = 1; k < 7 && one !== 255; k++) {
      const n = g.ring[7 * c + k];
      const b = n < 0 ? 0 : bandAt(bands, n);
      if (b === 0 || b === one) continue;
      one = one === 0 ? b : 255;
    }
    out[c] = one;
  }
  return out;
}

/** The marching squares table: for each corner mask (bit 0 the corner
 *  at i,j, bit 1 at i+1,j, bit 2 at i+1,j+1, bit 3 at i,j+1), the edges
 *  each segment joins. Edge 0 is j, 1 is i+1, 2 is j+1, 3 is i. The two
 *  saddles (5 and 10) take the pair of the low middle; the trace swaps
 *  them on a high middle. */
const CASES: readonly (readonly number[])[] = [
  [], [3, 0], [0, 1], [3, 1], [1, 2], [3, 0, 1, 2], [0, 2], [3, 2],
  [2, 3], [0, 2], [0, 1, 2, 3], [1, 2], [1, 3], [0, 1], [3, 0], [],
];

/**
 * Traces the band edges of `bands` on a lattice of `step` world px. A
 * lattice point off the grid, or where no band reaches, has no value, and
 * a square with such a corner holds no segment.
 */
export function traceBandEdges(bands: Uint8Array, step: number = T.fig.chart_trace_px): EdgeTrace {
  const { w, h } = worldSize(COLS, ROWS);
  const s = Math.max(1, step);
  const nx = Math.ceil(w / s) + 1, ny = Math.ceil(h / s) + 1;
  const g = geo();
  const ring = ringBands(g, bands);
  // The field is flat on a cell whose ring holds one band, so an edge can
  // cross only a square near a cell of two bands. The trace marks those
  // squares and reads the field at their corners alone.
  const mark = new Uint8Array(nx * ny);
  const rx = HEX_SIZE * Math.sqrt(3) / 2 + s, ry = HEX_SIZE + s;
  for (let c = 0; c < ring.length; c++) {
    if (ring[c] !== 255) continue;
    const i0 = Math.max(0, Math.floor((g.cx[c] - rx) / s)), i1 = Math.min(nx - 2, Math.floor((g.cx[c] + rx) / s));
    const j0 = Math.max(0, Math.floor((g.cy[c] - ry) / s)), j1 = Math.min(ny - 2, Math.floor((g.cy[c] + ry) / s));
    for (let j = j0; j <= j1; j++) mark.fill(1, j * nx + i0, j * nx + i1 + 1);
  }
  // -1 is a corner not read yet; NaN a corner with no value.
  const v = new Float32Array(nx * ny).fill(-1);
  const out = new Float64Array(3);
  const at = (i: number, j: number): number => {
    const n = j * nx + i;
    if (v[n] !== -1) return v[n];
    const x = i * s, y = j * s;
    const c = cellAt(x, y, COLS, ROWS);
    let f = NaN;
    if (c >= 0) {
      const one = ring[c];
      // A cell with a band whose ring holds that band alone: the blend
      // of one band is that band. A cell with no band reads the kernel,
      // because its own cell adds nothing to the reach.
      if (one !== 0 && one !== 255 && bandAt(bands, c) !== 0) f = one;
      else if (one !== 0 && fieldIn(g, bands, c, x, y, out)) f = out[0];
    }
    v[n] = f;
    return f;
  };
  const segs: EdgeTrace['segs'] = [[], [], []];
  const ex = [0, 0, 0, 0], ey = [0, 0, 0, 0];
  for (let j = 0; j + 1 < ny; j++) {
    for (let i = 0; i + 1 < nx; i++) {
      if (mark[j * nx + i] === 0) continue;
      const a = at(i, j), b = at(i + 1, j);
      const c = at(i + 1, j + 1), d = at(i, j + 1);
      if (Number.isNaN(a) || Number.isNaN(b) || Number.isNaN(c) || Number.isNaN(d)) continue;
      const lo = Math.min(a, b, c, d), hi = Math.max(a, b, c, d);
      const x0 = i * s, y0 = j * s;
      for (let k = 0; k < EDGE_LEVELS.length; k++) {
        const lv = EDGE_LEVELS[k];
        if (lv <= lo || lv >= hi) continue;
        const mask = (a > lv ? 1 : 0) | (b > lv ? 2 : 0) | (c > lv ? 4 : 0) | (d > lv ? 8 : 0);
        let pairs = CASES[mask];
        if ((mask === 5 || mask === 10) && (a + b + c + d) / 4 > lv) pairs = CASES[15 - mask];
        // The point of the level on each edge of the square.
        ex[0] = x0 + s * (lv - a) / (b - a); ey[0] = y0;
        ex[1] = x0 + s; ey[1] = y0 + s * (lv - b) / (c - b);
        ex[2] = x0 + s * (lv - d) / (c - d); ey[2] = y0 + s;
        ex[3] = x0; ey[3] = y0 + s * (lv - a) / (d - a);
        for (let p = 0; p + 1 < pairs.length; p += 2) {
          const e0 = pairs[p], e1 = pairs[p + 1];
          const mx = (ex[e0] + ex[e1]) / 2, my = (ey[e0] + ey[e1]) / 2;
          if (bandAt(bands, cellAt(mx, my, COLS, ROWS)) === 0) continue;
          segs[k].push(ex[e0], ey[e0], ex[e1], ey[e1]);
        }
      }
    }
  }
  return { segs };
}

/** One figure on a band edge: the level index, the place and the unit
 *  tangent of the edge there, turned to read left to right. */
export interface EdgeFigure { k: number; x: number; y: number; tx: number; ty: number }

/**
 * The figures of a trace: each one on the middle of a segment, at least
 * `every` world px from every other figure of its edge and `clear` world
 * px from a figure of another edge, the first segment in the order of the
 * trace first. The tangent is the normal of the field gradient, so it
 * follows the smooth edge and not the lattice.
 */
export function placeFigures(
  bands: Uint8Array, trace: EdgeTrace,
  every: number = T.fig.chart_figure_every_px, clear: number = T.fig.chart_figure_clear_px,
): EdgeFigure[] {
  const out: EdgeFigure[] = [];
  const cell = Math.max(1, every, clear);
  const grid = new Map<number, EdgeFigure[]>();
  const key = (gx: number, gy: number) => gy * 65536 + gx;
  const g = new Float64Array(3);
  for (let k = 0; k < trace.segs.length; k++) {
    const s = trace.segs[k];
    for (let i = 0; i + 3 < s.length; i += 4) {
      const x = (s[i] + s[i + 2]) / 2, y = (s[i + 1] + s[i + 3]) / 2;
      const gx = Math.floor(x / cell), gy = Math.floor(y / cell);
      let near = false;
      for (let dy = -1; dy <= 1 && !near; dy++) {
        for (let dx = -1; dx <= 1 && !near; dx++) {
          for (const q of grid.get(key(gx + dx, gy + dy)) ?? []) {
            const keep = q.k === k ? cell : clear;
            if ((q.x - x) ** 2 + (q.y - y) ** 2 < keep * keep) { near = true; break; }
          }
        }
      }
      if (near) continue;
      let tx = s[i + 2] - s[i], ty = s[i + 3] - s[i + 1];
      if (bandFieldAt(bands, x, y, g) && Math.hypot(g[1], g[2]) > 1e-9) { tx = -g[2]; ty = g[1]; }
      const len = Math.hypot(tx, ty) || 1;
      tx /= len; ty /= len;
      if (tx < -1e-9) { tx = -tx; ty = -ty; }
      const f = { k, x, y, tx, ty };
      out.push(f);
      const at = key(gx, gy);
      const list = grid.get(at);
      if (list) list.push(f); else grid.set(at, [f]);
    }
  }
  return out;
}

/**
 * The segments of a trace with the gap of each figure taken out: a
 * segment of level k loses its part within `gaps[k] / 2` world px of a
 * figure of level k. Answers new arrays; the trace stays whole.
 */
export function cutGaps(
  trace: EdgeTrace, figures: readonly EdgeFigure[], gaps: readonly number[],
): EdgeTrace['segs'] {
  const out: EdgeTrace['segs'] = [[], [], []];
  for (let k = 0; k < trace.segs.length; k++) {
    const r = Math.max(0, (gaps[k] ?? 0) / 2);
    const mine = figures.filter((f) => f.k === k);
    const s = trace.segs[k];
    for (let i = 0; i + 3 < s.length; i += 4) {
      let parts: number[][] = [[s[i], s[i + 1], s[i + 2], s[i + 3]]];
      const mx = (s[i] + s[i + 2]) / 2, my = (s[i + 1] + s[i + 3]) / 2;
      const reach = r + Math.hypot(s[i + 2] - s[i], s[i + 3] - s[i + 1]) / 2;
      for (const f of mine) {
        if (r <= 0) break;
        // A figure out of reach of the whole segment cuts nothing.
        if (Math.abs(f.x - mx) > reach || Math.abs(f.y - my) > reach) continue;
        const next: number[][] = [];
        for (const p of parts) next.push(...clipOutside(p, f.x, f.y, r));
        parts = next;
        if (parts.length === 0) break;
      }
      for (const p of parts) out[k].push(p[0], p[1], p[2], p[3]);
    }
  }
  return out;
}

/** The parts of the segment p that stand outside the circle at cx, cy of
 *  radius r. */
function clipOutside(p: readonly number[], cx: number, cy: number, r: number): number[][] {
  const [x0, y0, x1, y1] = p;
  const dx = x1 - x0, dy = y1 - y0;
  const fx = x0 - cx, fy = y0 - cy;
  const a = dx * dx + dy * dy;
  if (a <= 0) return fx * fx + fy * fy >= r * r ? [[...p]] : [];
  const b = 2 * (fx * dx + fy * dy), c = fx * fx + fy * fy - r * r;
  const disc = b * b - 4 * a * c;
  if (disc <= 0) return [[...p]];
  const q = Math.sqrt(disc);
  const t0 = (-b - q) / (2 * a), t1 = (-b + q) / (2 * a);
  if (t1 <= 0 || t0 >= 1) return [[...p]];
  const out: number[][] = [];
  if (t0 > 0) out.push([x0, y0, x0 + dx * t0, y0 + dy * t0]);
  if (t1 < 1) out.push([x0 + dx * t1, y0 + dy * t1, x1, y1]);
  return out;
}
